import { useEffect, useRef, useState } from 'react';

/**
 * @typedef {Object} UseIsInViewportOnceReturnType
 * @property {import('react').RefObject<HTMLElement>} elementRef - the ref to hook into the element
 * @property {boolean} isInViewport
 */

/**
 * Track an elementRef is in view once, then trigger callback
 * @param {object} props
 * @param {boolean} props.shouldTrack
 * @param {CallableFunction} props.callback
 * @param {number} props.timeout
 * @param {number} props.threshold
 * @returns {UseIsInViewportOnceReturnType}
 */
export function useIsInViewportOnce({ shouldTrack = true, callback, timeout = 0, threshold = 0 }) {
  const [isInViewport, setIsInViewport] = useState(false);
  const elementRef = useRef();
  const timerRef = useRef();

  useEffect(() => {
    if (!shouldTrack) return;
    if (!elementRef.current) return;
    if (isInViewport) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        // If the item is in viewport, and user not scroll for at least xx miliseconds -> trigger callback
        clearTimeout(timerRef.current);
        if (entry.isIntersecting) {
          timerRef.current = setTimeout(callback, timeout);
          setIsInViewport(true);
          obs.unobserve(elementRef.current);
        }
      },
      { threshold },
    );

    if (elementRef.current) obs.observe(elementRef.current);

    return () => {
      if (elementRef.current) obs.unobserve(elementRef.current);
    };
  }, [shouldTrack, elementRef.current]);

  return { elementRef, isInViewport };
}
